import { $isMarkNode } from '@lexical/mark';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { CellAction, dispatchCellActions } from 'context/CellActions';
import { CellActionsContext } from 'context/CellActions/CellActionsContext';
import { $getNodeByKey, TextNode } from 'lexical';
import { selectAbbreviationsData } from 'modules/Abbreviations/store/selectors';
import { useCallback, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { registerCommands } from './commands';
import { $getAbbreviationData, $removeAbbreviationIfTermChanged } from './utils';

export { ABBREVIATION_CHANGE } from './commands';

export type LexicalAbbreviationValues = {
  abbreviationId: string | undefined;
  abbreviationTerm: string | undefined;
};

type Props = {
  onChange: (values: LexicalAbbreviationValues) => void;
};

export function AbbreviationPlugin(props: Props): null {
  const { onChange } = props;
  const { relationId } = useContext(CellActionsContext);
  const handleClick = useCallback((): void => dispatchCellActions(
    relationId,
    [CellAction.TOGGLE_EDITMODE_ON, true],
    CellAction.OPEN_ABBREVIATIONS_PANEL,
  ), [relationId]);

  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    registerCommands(editor, handleClick);
  }, [editor, handleClick]);

  useEffect(() => {
    const readEditorStateAndNotifyChange = (): void => {
      const { abbreviationId, abbreviationTerm } = $getAbbreviationData();
      onChange({
        abbreviationId,
        abbreviationTerm,
      });
    };
    editor.getEditorState().read(readEditorStateAndNotifyChange);

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(readEditorStateAndNotifyChange);
    });
  }, [editor, onChange]);

  const abbreviationsData = useSelector(selectAbbreviationsData);
  useEffect(() => {
    return editor.registerMutationListener(
      TextNode,
      (mutatedNodes) => {
        editor.update(() => {
          for (const [nodeKey, mutation] of mutatedNodes) {
            if (mutation !== 'updated') {
              continue;
            }
            const abbrNode = $getNodeByKey<TextNode>(nodeKey)?.getParent() ?? null;
            if (!$isMarkNode(abbrNode)) {
              continue;
            }
            $removeAbbreviationIfTermChanged(abbrNode, abbreviationsData);
          }
        });
      },
    );
  }, [editor, abbreviationsData]);

  return null;
}
